/**
 * Trovva — Studio tool definitions.
 *
 * Each tool declares:
 *   - key         stable id sent to /api/studio-generate
 *   - label       user-facing name
 *   - blurb       one-line explanation on the tool card
 *   - icon        lucide-react icon name (rendered in StudioView)
 *   - category    one of: listings | buyers | sellers | past_clients
 *   - fields      form schema — { name, label, type, placeholder?, required?, options?, rows? }
 *   - systemPrompt the Claude system prompt (backend reads from this file)
 *
 * All fields are strings or select options. Backend compiles a user
 * message from the filled form + tool.systemPrompt and calls Claude.
 */

export const CATEGORIES = [
  { key: 'listings',     label: 'For Listings',      emoji: '🏠', blurb: 'Descriptions, social posts, flyers, video scripts.' },
  { key: 'buyers',       label: 'For Buyers',        emoji: '🔑', blurb: 'Offer letters, preapproval explainers, neighborhood guides.' },
  { key: 'sellers',      label: 'For Sellers',       emoji: '📈', blurb: 'CMA narratives, market updates, presentation talking points.' },
  { key: 'past_clients', label: 'For Past Clients',  emoji: '🤝', blurb: 'Anniversary notes, market check-ins, referral asks.' },
];

// ── Field helpers ────────────────────────────────────────────────
const addressField = { name: 'address', label: 'Property address', type: 'text', placeholder: '1 Bank St, Bordentown, NJ', required: true };
const priceField   = { name: 'price', label: 'List price', type: 'text', placeholder: '$425,000', required: false };
const bedsField    = { name: 'beds', label: 'Beds', type: 'text', placeholder: '3', required: false };
const bathsField   = { name: 'baths', label: 'Baths', type: 'text', placeholder: '2', required: false };
const sqftField    = { name: 'sqft', label: 'Sq ft', type: 'text', placeholder: '1,800', required: false };
const featuresField= { name: 'features', label: 'Key features (comma-separated)', type: 'textarea', rows: 2, placeholder: 'Updated kitchen, hardwood floors, finished basement, new roof 2024', required: false };
const styleField   = { name: 'style', label: 'Tone / style', type: 'select', required: true, options: [
  { value: 'professional', label: 'Professional (MLS)' },
  { value: 'warm',         label: 'Warm & inviting' },
  { value: 'luxury',       label: 'Luxury / upscale' },
  { value: 'investor',     label: 'Investor-focused' },
  { value: 'friendly',     label: 'Friendly & conversational' },
]};

// ── Tool definitions ────────────────────────────────────────────
export const TOOLS = [
  // ─── For Listings ─────────────────────────────
  {
    key: 'listing_description',
    category: 'listings',
    label: 'MLS listing description',
    blurb: 'Polished MLS copy — pick your tone.',
    icon: 'Home',
    fields: [
      addressField, priceField, bedsField, bathsField, sqftField, featuresField, styleField,
      { name: 'purpose', label: 'For sale or for rent', type: 'select', required: true, options: [
        { value: 'sale', label: 'For Sale' },
        { value: 'rent', label: 'For Rent' },
      ]},
    ],
    systemPrompt: `You write MLS property descriptions for a real estate agent. 180-220 words. Lead with the single most compelling feature. Avoid clichés ("charming," "must-see," "won't last"). Use specific sensory detail from the features provided. End with a call to action — show / contact listing agent. Match the requested tone.`,
  },
  {
    key: 'listing_social',
    category: 'listings',
    label: 'Social media post',
    blurb: 'Multi-platform (Instagram / Facebook / X). Generates all three.',
    icon: 'Share2',
    fields: [
      addressField, priceField, bedsField, bathsField, sqftField, featuresField,
      { name: 'hook', label: 'Angle / hook (optional)', type: 'textarea', rows: 2, placeholder: 'e.g. "perfect for remote workers" or "great school district"', required: false },
    ],
    systemPrompt: `Write three social media posts — one each for Instagram, Facebook, and X (Twitter) — for a real estate listing. Format the output as three clearly labeled sections with "---" dividers. Instagram: 80-120 words, emoji-forward, hook in first line, hashtags at end (8-12 relevant tags). Facebook: 120-180 words, more conversational, link-friendly. X/Twitter: under 280 characters, punchy. No clichés.`,
  },
  {
    key: 'listing_flyer',
    category: 'listings',
    label: 'Flyer copy',
    blurb: 'Printable single-page flyer text.',
    icon: 'FileText',
    fields: [
      addressField, priceField, bedsField, bathsField, sqftField, featuresField,
      { name: 'highlights', label: 'Top 3 highlights to bold', type: 'textarea', rows: 2, placeholder: 'New HVAC 2024; walk to train; finished basement', required: false },
    ],
    systemPrompt: `Write flyer copy for a real estate listing. Output these sections in order: (1) HEADLINE — 6-10 words, punchy. (2) SUBHEAD — 1 sentence. (3) FEATURES — bulleted list of 5-8 selling points, short fragments. (4) CALL TO ACTION — 1-2 sentences directing to schedule a showing / contact listing agent. No cliches, specific language only.`,
  },
  {
    key: 'listing_video_script',
    category: 'listings',
    label: 'Video script (30s / 60s)',
    blurb: 'Voiceover or walk-through script.',
    icon: 'Video',
    fields: [
      addressField, featuresField,
      { name: 'duration', label: 'Length', type: 'select', required: true, options: [
        { value: '30', label: '30 seconds' },
        { value: '60', label: '60 seconds' },
        { value: '90', label: '90 seconds' },
      ]},
      { name: 'style_video', label: 'Delivery style', type: 'select', required: true, options: [
        { value: 'walkthrough', label: 'Walk-through (agent on camera)' },
        { value: 'voiceover',   label: 'Voiceover (over b-roll)' },
        { value: 'social_vertical', label: 'Vertical short (TikTok / Reels)' },
      ]},
    ],
    systemPrompt: `Write a real-estate video script broken into timed shots. Output as a table or timestamped list: [0:00] shot description — voiceover text. Total runtime matches the requested duration. Aim for 2.5 words per second for voiceover. Walk-through style: include 1-2 lines of agent on-camera intro + outro; rest is narration over rooms. Voiceover style: no on-camera. Vertical short: fast cuts, 2-4 second shots max, hook in first 2 seconds.`,
  },

  // ─── For Buyers ───────────────────────────────
  {
    key: 'buyer_offer_letter',
    category: 'buyers',
    label: 'Offer cover letter',
    blurb: 'Personal letter from buyer to seller — swings tie-breaker offers.',
    icon: 'Mail',
    fields: [
      { name: 'buyer_names', label: "Buyer(s) name(s)", type: 'text', placeholder: 'James & Kim Wilson', required: true },
      addressField,
      { name: 'offer_price', label: 'Offer price', type: 'text', placeholder: '$415,000', required: false },
      { name: 'why_love', label: "What buyer loves about this house (specific)", type: 'textarea', rows: 3, placeholder: 'kitchen sunlight, backyard size, walk to kids school', required: true },
      { name: 'buyer_story', label: 'Buyer background (1-2 sentences)', type: 'textarea', rows: 2, placeholder: 'First-time buyers, just got engaged, both work from home', required: false },
    ],
    systemPrompt: `Write a personal offer cover letter from the buyer to the seller. 220-280 words. FIRST person from the buyer. Warm but not saccharine. Specific, sensory details from what they love. Brief human background — 2 sentences max. Mention the offer price only if provided. End with a sincere paragraph about what this home would mean. No clichés ("we fell in love the moment we walked in"). Sign-off: "Warmly, <buyer names>".`,
  },
  {
    key: 'buyer_preapproval_explainer',
    category: 'buyers',
    label: 'Preapproval explainer',
    blurb: 'Plain-English walkthrough for nervous first-time buyers.',
    icon: 'CheckSquare',
    fields: [
      { name: 'client_first_name', label: 'Client first name', type: 'text', placeholder: 'Sarah', required: true },
      { name: 'lender_name', label: 'Lender name (optional)', type: 'text', placeholder: 'Rocket Mortgage', required: false },
      { name: 'buyer_concerns', label: 'Specific concerns (optional)', type: 'textarea', rows: 2, placeholder: "doesn't understand DTI, worried about credit score, confused about rate lock", required: false },
    ],
    systemPrompt: `Write a plain-English email to a first-time buyer explaining the preapproval process. Assume they know nothing. Cover: what a preapproval is, why it matters (vs prequalification), what documents they'll need, what happens after, how long it's valid. Address any specific concerns the client raised. 300-380 words. Tone: warm, patient, not condescending. Sign off with "let me know what questions come up — no question is a bad one."`,
  },
  {
    key: 'buyer_neighborhood_guide',
    category: 'buyers',
    label: 'Neighborhood guide',
    blurb: 'Why your client should love this area — schools, commute, vibe.',
    icon: 'MapPin',
    fields: [
      { name: 'neighborhood', label: 'Neighborhood / town', type: 'text', placeholder: 'Bordentown, NJ', required: true },
      { name: 'buyer_priorities', label: "Buyer priorities (what they care about)", type: 'textarea', rows: 2, placeholder: 'walkable, good schools, 30-min commute to Philly, under $500K', required: false },
      { name: 'agent_insight', label: "Your local insight (optional)", type: 'textarea', rows: 2, placeholder: "best coffee shop, quiet streets on east side, new development coming to Farnsworth Ave", required: false },
    ],
    systemPrompt: `Write a neighborhood guide as an email to a prospective buyer. Structure: (1) Opening — 1-2 sentence personalized intro. (2) The vibe — short paragraph, what makes this place feel like itself. (3) Practical — schools, commute, grocery/pharmacy, dining, nearest hospital. (4) Hidden gems — 2-3 specific local tips (weave in agent's local insight if provided). (5) Price reality — general sense of the market at the buyer's price band. (6) Close — next-step CTA. 350-450 words. Avoid generic language ("charming tree-lined streets"). If agent didn't provide insight, keep practical facts conservative — don't fabricate specific businesses.`,
  },

  // ─── For Sellers ──────────────────────────────
  {
    key: 'seller_cma_narrative',
    category: 'sellers',
    label: 'CMA narrative',
    blurb: 'Turn raw comps into a persuasive price-rationale story.',
    icon: 'TrendingUp',
    fields: [
      addressField,
      { name: 'subject_details', label: 'Subject property key details', type: 'textarea', rows: 2, placeholder: '3 bed / 2 bath / 1,800 sqft / updated kitchen 2023 / detached garage', required: true },
      { name: 'comps', label: 'Comps (address + price + date + notes, one per line)', type: 'textarea', rows: 5, placeholder: `85 Maple Ave — $435K — sold Mar 2026 — similar size, older kitchen\n92 Oak St — $410K — sold Feb 2026 — smaller lot\n12 Pine Dr — $448K — sold Jan 2026 — renovated basement`, required: true },
      { name: 'recommended_price', label: 'Recommended list price', type: 'text', placeholder: '$425,000', required: true },
    ],
    systemPrompt: `Write a CMA narrative for a seller. You are explaining why the recommended price is right, using the comps provided. Structure: (1) Headline — "Your home's market value: $X–$Y". (2) What comps tell us — 2-3 paragraphs walking through the comps, comparing each to the subject property, noting what each adds or subtracts. (3) The recommendation — paragraph explaining the strategy behind the listed number (aggressive / balanced / conservative). (4) What to expect — timeline, likely buyer pool, negotiation range. 400-500 words. Tone: confident, data-driven, never salesy.`,
  },
  {
    key: 'seller_market_update',
    category: 'sellers',
    label: 'Market update',
    blurb: 'Monthly / quarterly update to send to sellers who paused.',
    icon: 'BarChart3',
    fields: [
      { name: 'market_area', label: 'Market area', type: 'text', placeholder: 'Burlington County, NJ', required: true },
      { name: 'period', label: 'Period', type: 'select', required: true, options: [
        { value: 'month',   label: 'Past month' },
        { value: 'quarter', label: 'Past quarter' },
        { value: 'year',    label: 'Year to date' },
      ]},
      { name: 'market_facts', label: 'Key data points (one per line)', type: 'textarea', rows: 4, placeholder: 'median price $412K, up 3% YoY\ninventory down 12%\navg days on market 28\nsale-to-list ratio 101%', required: true },
      { name: 'audience', label: 'Audience', type: 'select', required: true, options: [
        { value: 'past_sellers',     label: 'Past sellers / paused listings' },
        { value: 'prospective',      label: 'Prospective sellers (farming)' },
        { value: 'owner_landlords',  label: 'Owner-landlords' },
      ]},
    ],
    systemPrompt: `Write a market update email. Lead with the one number that most matters to this audience. Walk through 3-4 data points with context (is this up/down? what does it mean for them?). End with a soft CTA appropriate to the audience (past sellers: "if you're reconsidering, here's what we'd do differently now"; prospective: "want a specific analysis of your home?"; owner-landlords: "considering selling vs holding? I can run the numbers"). 280-340 words. Professional but not dry.`,
  },
  {
    key: 'seller_presentation_points',
    category: 'sellers',
    label: 'Listing presentation talking points',
    blurb: 'Your winning outline for the living-room pitch.',
    icon: 'ClipboardList',
    fields: [
      addressField,
      { name: 'seller_motivation', label: 'Seller motivation / situation', type: 'textarea', rows: 2, placeholder: 'Empty-nesters downsizing. Want to be in FL by October.', required: true },
      { name: 'price_expectation', label: "Seller's price expectation vs CMA", type: 'textarea', rows: 2, placeholder: 'Seller wants $475K. CMA supports $430-445K. Need to bridge.', required: false },
      { name: 'competition', label: 'Competing agents (if known)', type: 'text', placeholder: 'Jane Doe at Compass', required: false },
    ],
    systemPrompt: `Draft a structured outline for a 30-45 minute listing-presentation meeting. Format as a numbered agenda with talking points + suggested language under each. Sections: (1) Connect & acknowledge — 3-5 min. (2) Understand their situation — 5-10 min of questions. (3) Market overview specific to their area. (4) CMA + pricing strategy — this is where the price conversation lives. (5) Marketing plan specific to this property. (6) Why me/us — 3 concrete reasons tailored to their motivation. (7) Close — ask for the listing. If a price gap was mentioned, include specific scripts for handling it. If a competitor was named, include a brief "how we're different" without disparaging them.`,
  },

  // ─── For Past Clients ─────────────────────────
  {
    key: 'past_anniversary',
    category: 'past_clients',
    label: 'Home anniversary note',
    blurb: '1-year / 5-year anniversary of closing — warm, specific.',
    icon: 'Gift',
    fields: [
      { name: 'client_name_past', label: "Client(s) name(s)", type: 'text', placeholder: 'James & Kim Wilson', required: true },
      { name: 'address_past', label: 'Home address', type: 'text', placeholder: '1 Bank St, Bordentown, NJ', required: true },
      { name: 'years_ago', label: 'Years since closing', type: 'text', placeholder: '1', required: true },
      { name: 'memory', label: 'Something specific from their transaction (optional)', type: 'textarea', rows: 2, placeholder: 'We were worried about the well inspection. You bought right before the rate hike.', required: false },
    ],
    systemPrompt: `Write a brief anniversary note (120-180 words) to a past client marking their home-purchase anniversary. FIRST person from the agent. Warm but not cloying. If a memory from their transaction was provided, weave it in specifically. End with a light, no-pressure check-in — "how's the place treating you?" — NOT a referral ask. Sign off warmly.`,
  },
  {
    key: 'past_market_checkin',
    category: 'past_clients',
    label: 'Market check-in',
    blurb: "Here's what your home is worth now — sent to past buyers.",
    icon: 'RefreshCw',
    fields: [
      { name: 'client_name_check', label: 'Client name', type: 'text', placeholder: 'Sarah Mitchell', required: true },
      { name: 'original_price', label: 'Price they paid', type: 'text', placeholder: '$385,000', required: false },
      { name: 'current_estimate', label: 'Estimated current value', type: 'text', placeholder: '$435,000', required: false },
      { name: 'market_context', label: 'Market context (1-2 sentences)', type: 'textarea', rows: 2, placeholder: 'Prices in your area up 12% since you bought. Inventory is tight.', required: false },
    ],
    systemPrompt: `Write a brief market check-in email to a past client (140-180 words). Share a rough estimate of their home's current value and the market context. Tone: informational, not pushy. NO hard sell. Close with "let me know if you'd ever want a formal valuation — no obligation." Sign off warmly.`,
  },
  {
    key: 'past_referral_ask',
    category: 'past_clients',
    label: 'Referral ask',
    blurb: 'Gracefully ask a happy past client for a referral.',
    icon: 'Users',
    fields: [
      { name: 'client_name_ref', label: 'Client name', type: 'text', placeholder: 'Sarah Mitchell', required: true },
      { name: 'specific_success', label: 'A specific success in their transaction (optional)', type: 'textarea', rows: 2, placeholder: 'We got them $15K under list in a multi-offer situation.', required: false },
      { name: 'audience_fit', label: 'Who should they refer? (optional)', type: 'text', placeholder: 'first-time buyers in Burlington County', required: false },
    ],
    systemPrompt: `Write a short referral-ask message (100-140 words) to a past client. Rules: NEVER make it feel transactional. Start with a genuine check-in, not a favor. Mention a specific moment from their transaction if provided. Ask for referrals as a natural part of the message, not the lead. Close with gratitude. Sign off warmly. This is an email, not a text.`,
  },
];

export function getToolByKey(key) {
  return TOOLS.find(t => t.key === key);
}

export function toolsByCategory(categoryKey) {
  return TOOLS.filter(t => t.category === categoryKey);
}
